// Expenses.js - Complete Unified Component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiDeleteBin5Fill } from "react-icons/ri";
import Swal from 'sweetalert2';
import {
  FaMoneyBillWave,
  FaPlusCircle,
  FaFilter,
  FaCalendarAlt,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaRegComment,
  FaTimes,
  FaFileInvoiceDollar,
  FaPaperclip,
  FaEdit,
  FaCheck,
} from "react-icons/fa";
import { FaShoppingCart } from "react-icons/fa";
import { MdOutlineArrowDropDown, MdOutlineFileDownload } from "react-icons/md";
import './Expenses.css';

// ============================================================
// API HELPERS
// ============================================================
const API_BASE = 'https://super-backend.hira-tech.co.uk/api';
const BACKEND_BASE_URL = 'https://super-backend.hira-tech.co.uk'; // ✅ Added for images/media

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  const shopId =
    localStorage.getItem('selectedShopId') ||
    localStorage.getItem('selected_shop_id') ||
    '';
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'X-Shop-Id': shopId,
    'Accept': 'application/json',
  };
};

const generateExpenseNumber = () => {
  const ts = Date.now().toString().slice(-8);
  const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `EXP-${ts}${rand}`;
};

const normalizeSupplier = (s) => {
  if (s == null) return null;
  if (typeof s === 'string') return { value: s, label: s };
  const label =
    s.supplier ||
    s.supplier_display_name ||
    s.company_name ||
    s.name ||
    'N/A';
  const value = s.id ?? label;
  return { value, label };
};

const mapRowFromBackend = (r) => {
  const rawLines = (r.items || []).map((l, i) => ({
    id: l.id ?? i + 1,
    category: l.category || '',
    description: l.description || '',
    amount: l.totalAmount != null ? String(l.totalAmount) : '',
  }));

  const rawFormData = {
    supplier: r.supplier || '',
    billDate: r.date || '',
    dueDate: r.due_date || '',
    billNo: r.number || '',
    payee: r.payee || '',
    paymentAccount: r.payment_account || 'Cash on hand',
    paymentDate: r.date || '',
    paymentMethod: r.payment_method || '',
    refNo: r.ref_no || '',
    memo: r.memo || '',
    attachmentName: r.file_path || '',
    shop: r.shop || '',
    city: r.city || '',
    lines: rawLines.length
      ? rawLines
      : [{ id: 1, category: r.category && r.category !== '-' ? r.category : '', description: '', amount: r.amount ? String(r.amount) : '' }],
  };

  const toInputDate = (d) => {
    if (!d) return '';
    if (d.includes('-')) {
      const parts = d.split('-');
      if (parts.length === 3 && parts[0].length === 2) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return d;
  };

  return {
    id: r.id,
    date: r.date || '',
    type: r.type || 'Expense',
    number: r.ref_no || r.no || r.expense_number || '',
    payee: r.payee || '',
    category: r.category || '-',
    total: r.total || '£0.00',
    status: r.status || 'Paid',
    class: '-',
    location: '-',
    attachmentName: r.file_path || '',
    attachmentUrl: r.file_path || '',
    attachment: null,
    rawFormData: { ...rawFormData, paymentDate: toInputDate(rawFormData.paymentDate) },
  };
};

const buildExpensePayload = (form, isEdit) => {
  const firstLine = form.lines[0] || {};

  const payload = {
    supplier: form.payee || '',
    date: form.paymentDate || new Date().toISOString().split('T')[0],
    memo: form.memo || '',
    shop: form.shop ? parseInt(form.shop, 10) : null,
    city: form.city || null,
    status: 'Paid',
    method: form.paymentMethod || '',
    ref_no: form.refNo || '',
    items: form.lines.map((line, index) => ({
      rowNumber: index + 1,
      category: line.category || 'General',
      description: line.description || '',
      totalAmount: parseFloat(line.amount) || 0,
    })),
    terms: firstLine.category || null,
  };

  if (!isEdit) {
    payload.expense_number = generateExpenseNumber();
  }

  return payload;
};

const buildBillPayload = (form, isEdit) => {
  const firstLine = form.lines[0] || {};

  const payload = {
    supplier: form.supplier || '',
    date: form.billDate || new Date().toISOString().split('T')[0],
    memo: form.memo || '',
    shop: form.shop ? parseInt(form.shop, 10) : null,
    city: form.city || null,
    status: 'Pending',
    ref_no: form.billNo || '',
    items: form.lines.map((line, index) => ({
      rowNumber: index + 1,
      category: line.category || 'General',
      description: line.description || '',
      totalAmount: parseFloat(line.amount) || 0,
    })),
    terms: firstLine.category || null,
  };

  if (!isEdit) {
    payload.expense_number = generateExpenseNumber();
  }

  return payload;
};

const buildFormDataFromPayload = (payload, file) => {
  const fd = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'items') {
      fd.append('items', JSON.stringify(value));
    } else if (value !== null && value !== undefined && value !== '') {
      fd.append(key, value);
    }
  });

  if (file) {
    fd.append('file', file);
  }

  return fd;
};

const Expenses = () => {
  const navigate = useNavigate();

  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showPayBillsDropdown, setShowPayBillsDropdown] = useState(false);
  const [showNewTransactionDropdown, setShowNewTransactionDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [showNewExpenseModal, setShowNewExpenseModal] = useState(false);
  const [showNewBillModal, setShowNewBillModal] = useState(false);

  const [editingRowId, setEditingRowId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [expensesData, setExpensesData] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const triggerReload = () => setReloadKey((k) => k + 1);

  const [shopsList, setShopsList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setExpensesLoading(true);
      try {
        const res = await fetch(`${API_BASE}/expenses/?page_size=200`, {
          headers: getAuthHeaders(),
        });
        const text = await res.text();
        if (!res.ok) throw new Error(`Server error: ${res.status}`);

        let data;
        try { data = JSON.parse(text); }
        catch (parseError) { throw new Error("Server returned HTML instead of JSON."); }

        const rows = Array.isArray(data) ? data : data.results || [];
        if (!cancelled) setExpensesData(rows.map(mapRowFromBackend));
      } catch (err) {
        console.error('Load expenses error:', err);
        if (!cancelled) {
          Swal.fire({ icon: 'error', title: 'Load failed', text: err.message || 'Could not load expenses.' });
        }
      } finally {
        if (!cancelled) setExpensesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const res = await fetch(`${API_BASE}/expenses/shops/`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setShopsList(Array.isArray(data) ? data : data.results || []);
        }
      } catch (err) {
        console.error("Shops fetch error:", err);
      }
    };
    fetchShops();
  }, []);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const shopId =
          localStorage.getItem('selectedShopId') ||
          localStorage.getItem('selected_shop_id') ||
          '';
        const qs = shopId ? `?shop_id=${encodeURIComponent(shopId)}` : '';
        const res = await fetch(`${API_BASE}/suppliers/${qs}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to load suppliers');
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : data.results || data.data || [];
        setSuppliersList(list);
      } catch (err) {
        console.error('Supplier fetch error:', err);
        setSuppliersList([]);
      } finally {
        setSuppliersLoading(false);
      }
    };
    fetchSuppliers();
  }, []);

  const [billForm, setBillForm] = useState({
    supplier: '',
    shop: '',
    city: '',
    billDate: '',
    dueDate: '',
    billNo: '',
    memo: '',
    attachment: null,
    attachmentName: '',
    attachmentUrl: '',
    lines: [
      { id: 1, category: '', description: '', amount: '' },
      { id: 2, category: '', description: '', amount: '' },
    ],
  });

  const [expenseForm, setExpenseForm] = useState({
    payee: '',
    shop: '',
    city: '',
    paymentAccount: 'Cash on hand',
    paymentDate: '',
    paymentMethod: '',
    refNo: '',
    memo: '',
    attachment: null,
    attachmentName: '',
    attachmentUrl: '',
    lines: [
      { id: 1, category: '', description: '', amount: '' },
      { id: 2, category: '', description: '', amount: '' },
    ],
  });

  const [editingLineId, setEditingLineId] = useState(null);
  const [editFormData, setEditFormData] = useState({ category: '', description: '', amount: '' });

  const [filterDates, setFilterDates] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [filterExpenseDate, setFilterExpenseDate] = useState('all');

  const [settings, setSettings] = useState({
    showAllTransactions: true,
    showDate: true,
    showType: true,
    showNumber: true,
    showPayee: true,
    showClass: false,
    showLocation: false,
    showStatus: false
  });

  const supplierOptions = [...new Set(expensesData.map(item => item.payee).filter(Boolean))];

  const getShopPrefix = () => {
    const shopMatch = window.location.pathname.match(/^\/shop\/(\d+)/);
    return shopMatch ? `/shop/${shopMatch[1]}` : '';
  };

  const transactionTypes = [
    { name: 'Expense', route: 'expense-modal' },
    { name: 'Bill', route: 'bill-modal' },
    { name: 'Cheque', route: '/cheque/new' },
    { name: 'Purchase Order', route: '/purchase-order/new' },
    { name: 'Supplier Credit', route: '/supplier-credit/new' },
    { name: 'Credit Card Credit', route: '/credit-card-credit/new' },
    { name: 'Pay Down Credit Card', route: '/pay-down-credit-card/new' },
    { name: 'Import Bills', route: '/import-bills/new' },
    { name: 'Supplier refund', route: '/supplier-refund/new' }
  ];

  const payBillsRef = useRef(null);
  const newTransactionRef = useRef(null);
  const payBillsBtnRef = useRef(null);
  const newTransactionBtnRef = useRef(null);
  const feedbackBtnRef = useRef(null);
  const filterBtnRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const filterModalRef = useRef(null);
  const dateBtnRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const settingsDropdownRef = useRef(null);
  const expenseModalRef = useRef(null);
  const billModalRef = useRef(null);

  const billFileInputRef = useRef(null);
  const expenseFileInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (payBillsBtnRef.current && !payBillsBtnRef.current.contains(event.target) &&
          payBillsRef.current && !payBillsRef.current.contains(event.target)) {
        setShowPayBillsDropdown(false);
      }
      if (newTransactionBtnRef.current && !newTransactionBtnRef.current.contains(event.target) &&
          newTransactionRef.current && !newTransactionRef.current.contains(event.target)) {
        setShowNewTransactionDropdown(false);
      }
      if (filterBtnRef.current && !filterBtnRef.current.contains(event.target) &&
          filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
      if (dateBtnRef.current && !dateBtnRef.current.contains(event.target) &&
          dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (settingsBtnRef.current && !settingsBtnRef.current.contains(event.target) &&
          settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCitiesForShop = async (shopId) => {
    if (!shopId) {
      setCitiesList([]);
      return;
    }
    setCitiesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/expenses/shop/${shopId}/cities/`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCitiesList(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Cities fetch error:", err);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleBillShopChange = (e) => {
    const shopId = e.target.value;
    setBillForm(prev => ({ ...prev, shop: shopId, city: '' }));
    fetchCitiesForShop(shopId);
  };

  const handleExpenseShopChange = (e) => {
    const shopId = e.target.value;
    setExpenseForm(prev => ({ ...prev, shop: shopId, city: '' }));
    fetchCitiesForShop(shopId);
  };

  const handlePrint = () => window.print();
  const handleSettingChange = (settingName) => setSettings(prev => ({ ...prev, [settingName]: !prev[settingName] }));

  const settingsOptions = [
    { key: 'showDate', label: 'Show Date' },
    { key: 'showType', label: 'Show Type' },
    { key: 'showNumber', label: 'Show Number' },
    { key: 'showPayee', label: 'Show Payee' },
    { key: 'showClass', label: 'Show Class' },
    { key: 'showLocation', label: 'Show Location' },
    { key: 'showStatus', label: 'Show Status' }
  ];

  const handleFeedback = () => navigate('/feedback');
  const handlePayBills = () => { setShowPayBillsDropdown(!showPayBillsDropdown); setShowNewTransactionDropdown(false); };
  const handleNewTransaction = () => { setShowNewTransactionDropdown(!showNewTransactionDropdown); setShowPayBillsDropdown(false); };
  const handleMakeRecurringPayment = () => { alert('Make Recurring Payment'); setShowPayBillsDropdown(false); };

  const handleTransactionType = (type, route) => {
    setShowNewTransactionDropdown(false);
    if (route === 'expense-modal') {
      setEditingRowId(null);
      resetExpenseForm();
      setShowNewExpenseModal(true);
    } else if (route === 'bill-modal') {
      setEditingRowId(null);
      resetBillForm();
      setShowNewBillModal(true);
    } else if (route) {
      const shopPrefix = getShopPrefix();
      navigate(shopPrefix ? `${shopPrefix}${route}` : route);
    } else {
      alert(`Create new ${type}`);
    }
  };

  const resetBillForm = () => {
    setBillForm({
      supplier: '', shop: '', city: '', billDate: '', dueDate: '', billNo: '', memo: '', attachment: null, attachmentName: '', attachmentUrl: '',
      lines: [{ id: 1, category: '', description: '', amount: '' }, { id: 2, category: '', description: '', amount: '' }],
    });
    setCitiesList([]);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      payee: '', shop: '', city: '', paymentAccount: 'Cash on hand', paymentDate: '', paymentMethod: '', refNo: '', memo: '', attachment: null, attachmentName: '', attachmentUrl: '',
      lines: [{ id: 1, category: '', description: '', amount: '' }, { id: 2, category: '', description: '', amount: '' }],
    });
    setCitiesList([]);
  };

  const handleBillInputChange = (e) => {
    const { name, value } = e.target;
    setBillForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBillLineChange = (id, field, value) => {
    setBillForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => line.id === id ? { ...line, [field]: value } : line),
    }));
  };

  const addBillLine = () => {
    const newId = billForm.lines.length > 0 ? Math.max(...billForm.lines.map((l) => l.id)) + 1 : 1;
    setBillForm((prev) => ({ ...prev, lines: [...prev.lines, { id: newId, category: '', description: '', amount: '' }] }));
  };

  const removeBillLine = (id) => {
    setBillForm((prev) => ({ ...prev, lines: prev.lines.filter((line) => line.id !== id) }));
  };

  const clearAllBillLines = () => setBillForm((prev) => ({ ...prev, lines: [] }));

  const calculateBillTotal = () => {
    return billForm.lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0).toFixed(2);
  };

  const closeBillModal = () => {
    setShowNewBillModal(false);
    setEditingRowId(null);
    setEditingLineId(null);
    resetBillForm();
  };

  const handleBillAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBillForm((prev) => ({
        ...prev,
        attachment: file,
        attachmentName: file.name,
        attachmentUrl: URL.createObjectURL(file)
      }));
    }
  };

  const handleSaveBill = async () => {
    if (!billForm.supplier) {
      Swal.fire({ icon: 'warning', title: 'Missing Info', text: 'Please select a supplier.', confirmButtonColor: '#004aad' });
      return;
    }

    setSaving(true);
    try {
      const isEdit = !!editingRowId;
      const payload = buildBillPayload(billForm, isEdit);
      const fd = buildFormDataFromPayload(payload, billForm.attachment);

      const url = isEdit
        ? `${API_BASE}/expenses/${editingRowId}/`
        : `${API_BASE}/expenses/`;

      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.error('Backend error:', errBody);
        throw new Error(errBody.detail || 'Save failed');
      }

      triggerReload();
      Swal.fire({
        icon: 'success',
        title: isEdit ? 'Updated!' : 'Saved!',
        text: isEdit ? 'Bill updated successfully.' : 'Bill added successfully.',
        timer: 2000,
        showConfirmButton: false
      });
      closeBillModal();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Could not save bill.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNewBill = () => { handleSaveBill(); };

  const handleExpenseInputChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleExpenseLineChange = (id, field, value) => {
    setExpenseForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => line.id === id ? { ...line, [field]: value } : line),
    }));
  };

  const addExpenseLine = () => {
    const newId = expenseForm.lines.length > 0 ? Math.max(...expenseForm.lines.map((l) => l.id)) + 1 : 1;
    setExpenseForm((prev) => ({ ...prev, lines: [...prev.lines, { id: newId, category: '', description: '', amount: '' }] }));
  };

  const removeExpenseLine = (id) => {
    setExpenseForm((prev) => ({ ...prev, lines: prev.lines.filter((line) => line.id !== id) }));
  };

  const clearAllExpenseLines = () => setExpenseForm((prev) => ({ ...prev, lines: [] }));

  const calculateExpenseTotal = () => {
    return expenseForm.lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0).toFixed(2);
  };

  const closeExpenseModal = () => {
    setShowNewExpenseModal(false);
    setEditingRowId(null);
    setEditingLineId(null);
    resetExpenseForm();
  };

  const handleExpenseAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setExpenseForm((prev) => ({
        ...prev,
        attachment: file,
        attachmentName: file.name,
        attachmentUrl: URL.createObjectURL(file)
      }));
    }
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.payee) {
      Swal.fire({ icon: 'warning', title: 'Missing Info', text: 'Please enter Payee name.', confirmButtonColor: '#004aad' });
      return;
    }

    setSaving(true);
    try {
      const isEdit = !!editingRowId;
      const payload = buildExpensePayload(expenseForm, isEdit);
      const fd = buildFormDataFromPayload(payload, expenseForm.attachment);

      const url = isEdit
        ? `${API_BASE}/expenses/${editingRowId}/`
        : `${API_BASE}/expenses/`;

      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.error('Backend error:', errBody);
        throw new Error(errBody.detail || 'Save failed');
      }

      triggerReload();
      Swal.fire({
        icon: 'success',
        title: isEdit ? 'Updated!' : 'Saved!',
        text: isEdit ? 'Expense updated successfully.' : 'Expense added successfully.',
        timer: 2000,
        showConfirmButton: false
      });
      closeExpenseModal();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Could not save expense.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNewExpense = () => { handleSaveExpense(); };

  const handleStartEdit = (line, context) => {
    setEditingLineId(`${context}-${line.id}`);
    setEditFormData({ category: line.category, description: line.description, amount: line.amount });
  };

  const handleCancelEdit = () => {
    setEditingLineId(null);
    setEditFormData({ category: '', description: '', amount: '' });
  };

  const handleSaveEdit = (id, context) => {
    if (context === 'bill') {
      setBillForm((prev) => ({
        ...prev,
        lines: prev.lines.map((line) => line.id === id ? { ...line, ...editFormData } : line),
      }));
    } else if (context === 'expense') {
      setExpenseForm((prev) => ({
        ...prev,
        lines: prev.lines.map((line) => line.id === id ? { ...line, ...editFormData } : line),
      }));
    }
    setEditingLineId(null);
    setEditFormData({ category: '', description: '', amount: '' });
  };

  const handleEditRow = (row) => {
    setEditingRowId(row.id);

    if (row.type === 'Expense') {
      setExpenseForm({
        payee: row.payee || '',
        shop: row.rawFormData?.shop || '',
        city: row.rawFormData?.city || '',
        paymentAccount: row.rawFormData?.paymentAccount || 'Cash on hand',
        paymentDate: row.rawFormData?.paymentDate || '',
        paymentMethod: row.rawFormData?.paymentMethod || '',
        refNo: row.rawFormData?.refNo || '',
        memo: row.rawFormData?.memo || '',
        attachment: null,
        attachmentName: row.attachmentName || '',
        attachmentUrl: row.attachmentUrl || '',
        lines: row.rawFormData?.lines || [{ id: 1, category: row.category || '', description: '', amount: row.total?.replace('£', '') || '' }]
      });
      if (row.rawFormData?.shop) fetchCitiesForShop(row.rawFormData.shop);
      setShowNewExpenseModal(true);
    } else if (row.type === 'Bill') {
      setBillForm({
        supplier: row.rawFormData?.supplier || row.payee || '',
        shop: row.rawFormData?.shop || '',
        city: row.rawFormData?.city || '',
        billDate: row.date || '',
        dueDate: row.rawFormData?.dueDate || '',
        billNo: row.rawFormData?.refNo || '',
        memo: row.rawFormData?.memo || '',
        attachment: null,
        attachmentName: row.attachmentName || '',
        attachmentUrl: row.attachmentUrl || '',
        lines: row.rawFormData?.lines || [{ id: 1, category: row.category || '', description: '', amount: row.total?.replace('£', '') || '' }]
      });
      if (row.rawFormData?.shop) fetchCitiesForShop(row.rawFormData.shop);
      setShowNewBillModal(true);
    }
  };

  const handleDeleteRow = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await fetch(`${API_BASE}/expenses/${id}/`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok && res.status !== 204) throw new Error('Delete failed');
        triggerReload();
        Swal.fire('Deleted!', 'Your record has been deleted.', 'success');
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    });
  };

  const handleApplyFilter = () => {
    setShowFilterModal(false);
    alert(`Filter applied`);
  };

  const handleResetFilter = () => {
    setFilterDates('all'); setFilterSupplier('all'); setFilterExpenseDate('all');
    setSelectedFilter('all'); setSelectedDateRange('all');
    alert('Filters reset!');
  };

  const getFilteredData = () => {
    let filtered = [...expensesData];
    if (selectedFilter !== 'all') filtered = filtered.filter(item => item.type.toLowerCase() === selectedFilter.toLowerCase());
    if (selectedDateRange !== 'all') {
      const today = new Date();
      if (selectedDateRange === 'today') filtered = filtered.filter(item => item.date === today.toISOString().split('T')[0]);
      else if (selectedDateRange === 'week') {
        const weekAgo = new Date(today.setDate(today.getDate() - 7));
        filtered = filtered.filter(item => new Date(item.date) >= weekAgo);
      } else if (selectedDateRange === 'month') {
        const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
        filtered = filtered.filter(item => new Date(item.date) >= monthAgo);
      }
    }
    return filtered;
  };

  const currentData = getFilteredData();

  const renderExpensesTable = () => (
    <table className="expenses-table">
      <thead>
        <tr>
          {settings.showDate && <th>Date</th>}
          {settings.showType && <th>Type</th>}
          {settings.showNumber && <th>Number</th>}
          {settings.showPayee && <th>Payee</th>}
          <th>Category</th>
          {settings.showClass && <th>Class</th>}
          {settings.showLocation && <th>Location</th>}
          {settings.showStatus && <th>Status</th>}
          <th>Total</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {currentData.map((item) => (
          <tr key={item.id}>
            {settings.showDate && <td>{item.date}</td>}
            {settings.showType && <td><span className={`transaction-type ${item.type?.toLowerCase().replace(/\s/g, '-')}`}>{item.type}</span></td>}
            {settings.showNumber && <td>{item.number}</td>}
            {settings.showPayee && <td>{item.payee}</td>}
            <td>{item.category}</td>
            {settings.showClass && <td>{item.class}</td>}
            {settings.showLocation && <td>{item.location}</td>}
            {settings.showStatus && <td><span className={`status-badge ${item.status?.toLowerCase()}`}>{item.status}</span></td>}
            <td className={item.total?.startsWith('-') ? 'negative' : 'positive'}>{item.total}</td>
            <td>
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                <button className="edit-btn" onClick={() => handleEditRow(item)} style={{ padding: '4px 8px', background: '#e0f2fe', color: '#0284c7', border: 'none', borderRadius: '4px', cursor: 'pointer' }} title="Edit"><FaEdit /></button>
                <button className="delete-btn" onClick={() => handleDeleteRow(item.id)} style={{ padding: '4px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }} title="Delete"><RiDeleteBin5Fill /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="expenses-main">
      <div className="expenses-header-section">
        <div className="expenses-header-left">
          <h2><FaShoppingCart className="expenses-title-icon" /> Expenses</h2>
        </div>

        <div className="expenses-header-right">
          <div className="region-tabs-right">
            <button className="icon-btn" title="Export" onClick={() => alert('Export functionality coming soon!')}><MdOutlineFileDownload size="1.2em" /></button>
            <button className="icon-btn" title="Print" onClick={handlePrint}>
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M448 192V77.25c0-8.49-3.37-16.62-9.37-22.63L393.37 9.37c-6-6-14.14-9.37-22.63-9.37H96C78.33 0 64 14.33 64 32v160c-35.35 0-64 28.65-64 64v112c0 8.84 7.16 16 16 16h48v96c0 17.67 14.33 32 32 32h320c17.67 0 32-14.33 32-32v-96h48c8.84 0 16-7.16 16-16V256c0-35.35-28.65-64-64-64zm-64 256H128v-96h256v96zm0-224H128V64h192v48c0 8.84 7.16 16 16 16h48v96zm48 72c-13.25 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.74 24 24c0 13.25-10.75 24-24 24z"/></svg>
            </button>
            <div className="settings-wrapper">
              <button ref={settingsBtnRef} className="icon-btn settings-btn" title="Settings" onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}>
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"/></svg>
                <MdOutlineArrowDropDown className="settings-dropdown-icon" />
              </button>
              {showSettingsDropdown && (
                <div className="settings-dropdown" ref={settingsDropdownRef}>
                  <div className="settings-dropdown-list">
                    {settingsOptions.map((option) => (
                      <label key={option.key} className="settings-option">
                        <input type="checkbox" checked={settings[option.key]} onChange={() => handleSettingChange(option.key)} className="settings-checkbox" />
                        <span className="settings-label">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="expenses-action-buttons">
            <div className="button-wrapper">
              <button ref={feedbackBtnRef} className="action-btn feedback-btn" onClick={handleFeedback}><FaRegComment className="btn-icon" /> Feedback</button>
            </div>

            <div className="button-wrapper">
              <button ref={payBillsBtnRef} className="action-btn paybills-btn" onClick={handlePayBills}><FaMoneyBillWave className="btn-icon" /> Pay bills</button>
              {showPayBillsDropdown && (
                <div className="action-dropdown paybills-dropdown" ref={payBillsRef}>
                  <div className="dropdown-header"><span>Pay Bills</span></div>
                  <div className="dropdown-item-action" onClick={handleMakeRecurringPayment}>
                    <div className="dropdown-item-content">
                      <div className="dropdown-item-title">Make Recurring Payment</div>
                      <div className="dropdown-item-desc">Set up automated bill payments</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="button-wrapper">
              <button ref={newTransactionBtnRef} className="action-btn newtransaction-btn" onClick={handleNewTransaction}><FaPlusCircle className="btn-icon" /> New Transaction</button>
              {showNewTransactionDropdown && (
                <div className="action-dropdown transaction-dropdown" ref={newTransactionRef}>
                  <div className="dropdown-header"><span>New Transaction</span></div>
                  <div className="transaction-list">
                    {transactionTypes.map((type, index) => (
                      <div key={index} className="transaction-list-item" onClick={() => handleTransactionType(type.name, type.route)}>
                        <div className="transaction-list-name">{type.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="expenses-filters">
        <div className="filter-left">
          <div className="filter-wrapper">
            <div className="filter-dropdown" ref={filterBtnRef} onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
              <span>All transactions <MdOutlineArrowDropDown className="dropdown-icon" /></span>
            </div>
            {showFilterDropdown && (
              <div className="dropdown-menu filter-type-dropdown" ref={filterDropdownRef}>
                <div className="dropdown-item" onClick={() => { setSelectedFilter('all'); setShowFilterDropdown(false); }}>All Transactions</div>
                <div className="dropdown-item" onClick={() => { setSelectedFilter('expense'); setShowFilterDropdown(false); }}>Expenses</div>
                <div className="dropdown-item" onClick={() => { setSelectedFilter('bill'); setShowFilterDropdown(false); }}>Bills</div>
                <div className="dropdown-item" onClick={() => { setSelectedFilter('purchase'); setShowFilterDropdown(false); }}>Purchase Orders</div>
              </div>
            )}
          </div>

          <div className="filter-dropdown" onClick={() => setShowFilterModal(true)}><FaFilter className="filter-icon" /><span>Filter</span></div>

          <div className="filter-wrapper">
            <div className="date-filter" ref={dateBtnRef} onClick={() => setShowDateDropdown(!showDateDropdown)}>
              <FaCalendarAlt className="calendar-icon" />
              <span>Dates: {selectedDateRange === 'all' ? 'All Dates' : selectedDateRange === 'today' ? 'Today' : selectedDateRange === 'week' ? 'Last 7 Days' : 'Last 30 Days'}<MdOutlineArrowDropDown className="dropdown-icon" /></span>
            </div>
            {showDateDropdown && (
              <div className="dropdown-menu date-dropdown" ref={dateDropdownRef}>
                <div className="dropdown-item" onClick={() => { setSelectedDateRange('all'); setShowDateDropdown(false); }}>All Dates</div>
                <div className="dropdown-item" onClick={() => { setSelectedDateRange('today'); setShowDateDropdown(false); }}>Today</div>
                <div className="dropdown-item" onClick={() => { setSelectedDateRange('week'); setShowDateDropdown(false); }}>Last 7 Days</div>
                <div className="dropdown-item" onClick={() => { setSelectedDateRange('month'); setShowDateDropdown(false); }}>Last 30 Days</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilterModal && (
        <div className="filter-modal-overlay">
          <div className="filter-modal" ref={filterModalRef}>
            <div className="filter-modal-header"><h3>Filter</h3><button className="close-modal" onClick={() => setShowFilterModal(false)}><FaTimes /></button></div>
            <div className="filter-modal-body">
              <div className="filter-section"><label className="filter-label">Dates: All Dates</label><select className="filter-select" value={filterDates} onChange={(e) => setFilterDates(e.target.value)}><option value="all">All Dates</option><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="last7">Last 7 days</option><option value="last30">Last 30 days</option><option value="thisMonth">This month</option><option value="lastMonth">Last month</option></select></div>
              <div className="filter-section"><label className="filter-label">Supplier</label><select className="filter-select" value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)}><option value="all">All</option>{supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
              <div className="filter-section"><label className="filter-label">Expense date</label><select className="filter-select" value={filterExpenseDate} onChange={(e) => setFilterExpenseDate(e.target.value)}><option value="all">All Dates</option><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="last7">Last 7 days</option><option value="last30">Last 30 days</option><option value="thisMonth">This month</option></select></div>
            </div>
            <div className="filter-modal-footer"><button className="reset-filter-btn" onClick={handleResetFilter}>Reset</button><button className="apply-filter-btn" onClick={handleApplyFilter}>Apply</button></div>
          </div>
        </div>
      )}

      {/* ✅ NEW / EDIT BILL MODAL */}
      {showNewBillModal && (
        <div className="expense-modal-overlay">
          <div className="expense-modal" ref={billModalRef}>
            <div className="expense-modal-header">
              <div className="expense-modal-title"><h3>{editingRowId ? 'Edit Bill' : 'Bill'}</h3></div>
              <div className="expense-modal-header-right"><button className="modal-close-btn" onClick={closeBillModal}><FaTimes /></button></div>
            </div>

            <div className="expense-balance-bar"><span>BALANCE DUE</span><strong>£0.00</strong></div>

            <div className="expense-modal-body">
              <div className="modal-top-section">
                <div className="modal-form-row modal-form-row-4">
                  <div className="modal-form-group">
                    <label>Supplier</label>
                    <select name="supplier" value={billForm.supplier} onChange={handleBillInputChange} disabled={suppliersLoading}>
                      <option value="">{suppliersLoading ? 'Loading...' : 'Select supplier'}</option>
                      {suppliersList.length > 0
                        ? suppliersList.map((raw) => {
                            const s = normalizeSupplier(raw);
                            if (!s) return null;
                            return <option key={s.value} value={s.label}>{s.label}</option>;
                          })
                        : (!suppliersLoading && <option value="" disabled>No suppliers available</option>)}
                    </select>
                  </div>
                  <div className="modal-form-group"><label>Bill No.</label><input type="text" name="billNo" value={billForm.billNo} onChange={handleBillInputChange} placeholder="Enter bill number" /></div>
                  <div className="modal-form-group"><label>Bill Date</label><input type="date" name="billDate" value={billForm.billDate} onChange={handleBillInputChange} /></div>
                  <div className="modal-form-group"><label>Due Date</label><input type="date" name="dueDate" value={billForm.dueDate} onChange={handleBillInputChange} /></div>
                </div>

                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label>Shop</label>
                    <select name="shop" value={billForm.shop} onChange={handleBillShopChange}>
                      <option value="">Select a shop</option>
                      {shopsList.map(shop => (
                        <option key={shop.id} value={shop.id}>{shop.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label>City</label>
                    <select name="city" value={billForm.city} onChange={handleBillInputChange} disabled={!billForm.shop || citiesLoading}>
                      <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                      {citiesList.map(city => (
                        <option key={city.id} value={city.name}>{city.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-line-items-section">
                <div className="modal-line-items-box">
                  <table className="modal-line-items-table">
                    <thead><tr><th style={{ width: '40px' }}></th><th style={{ width: '40px' }}>#</th><th>CATEGORY</th><th>DESCRIPTION</th><th style={{ textAlign: 'right', width: '140px' }}>AMOUNT</th><th style={{ width: '120px' }}>ACTION</th></tr></thead>
                    <tbody>
                      {billForm.lines.map((line, index) => {
                        const isEditing = editingLineId === `bill-${line.id}`;
                        return (
                          <tr key={line.id}>
                            <td className="modal-drag-handle">⋮⋮</td>
                            <td>{index + 1}</td>

                            {isEditing ? (
                              <>
                                <td><input type="text" value={editFormData.category} onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })} placeholder="Category" autoFocus /></td>
                                <td><input type="text" value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} placeholder="Description" /></td>
                                <td><input type="number" value={editFormData.amount} onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })} placeholder="0.00" style={{ textAlign: 'right' }} /></td>
                              </>
                            ) : (
                              <>
                                <td><input type="text" value={line.category} onChange={(e) => handleBillLineChange(line.id, 'category', e.target.value)} placeholder="Category" /></td>
                                <td><input type="text" value={line.description} onChange={(e) => handleBillLineChange(line.id, 'description', e.target.value)} placeholder="Description" /></td>
                                <td><input type="number" value={line.amount} onChange={(e) => handleBillLineChange(line.id, 'amount', e.target.value)} placeholder="0.00" style={{ textAlign: 'right' }} /></td>
                              </>
                            )}

                            <td>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                {isEditing ? (
                                  <>
                                    <button className="modal-delete-line-btn" style={{ color: '#10b981' }} onClick={() => handleSaveEdit(line.id, 'bill')} title="Save"><FaCheck /></button>
                                    <button className="modal-delete-line-btn" style={{ color: '#6b7280' }} onClick={handleCancelEdit} title="Cancel"><FaTimes /></button>
                                  </>
                                ) : (
                                  <>
                                    <button className="modal-delete-line-btn" style={{ color: '#004aad' }} onClick={() => handleStartEdit(line, 'bill')} title="Edit"><FaEdit /></button>
                                    <button className="modal-delete-line-btn" onClick={() => removeBillLine(line.id)} title="Delete"><RiDeleteBin5Fill /></button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="modal-line-items-actions">
                  <div className="modal-action-buttons"><button className="modal-add-line-btn" onClick={addBillLine}>Add lines</button><button className="modal-clear-lines-btn" onClick={clearAllBillLines}>Clear all lines</button></div>
                  <div className="modal-total-section"><span>Total</span><strong>£{calculateBillTotal()}</strong></div>
                </div>
              </div>

              <div className="modal-bottom-section">
                <div className="modal-form-row">
                  <div className="modal-form-group"><label>Memo</label><textarea name="memo" value={billForm.memo} onChange={handleBillInputChange} placeholder="Add a memo..." rows={4} /></div>
                  <div className="modal-form-group">
                    <label>Attachment</label>
                    {billForm.attachmentName ? (
                      <div className="attachment-preview-box">
                        {/* ✅ FIXED: For both local blob (new upload) and backend server (saved files) */}
                        <a
                          href={
                            billForm.attachmentUrl
                              ? (billForm.attachmentUrl.startsWith('blob:')
                                  ? billForm.attachmentUrl
                                  : `${BACKEND_BASE_URL}${billForm.attachmentUrl}`)
                              : '#'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-name"
                          style={{ color: '#004aad', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {billForm.attachmentName}
                        </a>
                        <div className="attachment-actions">
                          <button className="att-btn replace" onClick={() => billFileInputRef.current?.click()}>Change</button>
                        </div>
                        <input type="file" ref={billFileInputRef} onChange={handleBillAttachmentChange} style={{ display: 'none' }} />
                      </div>
                    ) : (
                      <label className="modal-attachment-box" htmlFor="bill-attachment">
                        <div className="modal-attachment-dropzone"><FaPaperclip className="modal-attach-icon" /><p>Add attachment</p><span>Max file size: 20 MB</span></div>
                        <input id="bill-attachment" type="file" onChange={handleBillAttachmentChange} style={{ display: 'none' }} />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="expense-modal-footer">
              <button className="modal-footer-btn modal-cancel-btn" onClick={closeBillModal}>Cancel</button>
              <div className="modal-footer-right">
                <button className="modal-footer-btn modal-save-btn" onClick={handleSaveBill} disabled={saving}>{saving ? 'Saving...' : (editingRowId ? 'Update' : 'Save')}</button>
                {!editingRowId && (
                  <button className="modal-footer-btn modal-save-new-btn" onClick={handleSaveAndNewBill} disabled={saving}>Save and new <span className="modal-arrow">▼</span></button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW / EDIT EXPENSE MODAL */}
      {showNewExpenseModal && (
        <div className="expense-modal-overlay">
          <div className="expense-modal" ref={expenseModalRef}>

            <div className="expense-modal-header">
              <div className="expense-modal-title"><h3>{editingRowId ? 'Edit Expense' : 'Expense'}</h3></div>
              <div className="expense-modal-header-right"><button className="modal-close-btn" onClick={closeExpenseModal}><FaTimes /></button></div>
            </div>

            <div className="expense-amount-bar">
              <span>AMOUNT</span>
              <strong>£{calculateExpenseTotal()}</strong>
            </div>

            <div className="expense-modal-body">
              <div className="modal-top-section">
                <div className="modal-form-row">

                  <div className="modal-form-group" style={{ flex: 2 }}>
                    <label>Payee</label>
                    <input
                      type="text"
                      name="payee"
                      value={expenseForm.payee}
                      onChange={handleExpenseInputChange}
                      placeholder="Who did you pay?"
                      style={{ flex: 1 }}
                    />
                  </div>

                  <div className="modal-form-group">
                    <label>Payment account</label>
                    <select name="paymentAccount" value={expenseForm.paymentAccount} onChange={handleExpenseInputChange}>
                      <option value="Cash on hand">Cash on hand</option>
                      <option value="Bank Account">Bank Account</option>
                    </select>
                  </div>
                </div>

                <div className="modal-form-row">
                  <div className="modal-form-group"><label>Payment Date</label><input type="date" name="paymentDate" value={expenseForm.paymentDate} onChange={handleExpenseInputChange} /></div>
                  <div className="modal-form-group"><label>Payment Method</label><input type="text" name="paymentMethod" value={expenseForm.paymentMethod} onChange={handleExpenseInputChange} placeholder="Method" /></div>
                  <div className="modal-form-group"><label>Ref no.</label><input type="text" name="refNo" value={expenseForm.refNo} onChange={handleExpenseInputChange} placeholder="Reference number" /></div>
                </div>

                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label>Shop</label>
                    <select name="shop" value={expenseForm.shop} onChange={handleExpenseShopChange}>
                      <option value="">Select a shop</option>
                      {shopsList.map(shop => (
                        <option key={shop.id} value={shop.id}>{shop.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label>City</label>
                    <select name="city" value={expenseForm.city} onChange={handleExpenseInputChange} disabled={!expenseForm.shop || citiesLoading}>
                      <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                      {citiesList.map(city => (
                        <option key={city.id} value={city.name}>{city.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-line-items-section">
                <div className="modal-line-items-box">
                  <table className="modal-line-items-table">
                    <thead><tr><th style={{ width: '40px' }}></th><th style={{ width: '40px' }}>#</th><th>CATEGORY</th><th>DESCRIPTION</th><th style={{ textAlign: 'right', width: '140px' }}>AMOUNT</th><th style={{ width: '120px' }}>ACTION</th></tr></thead>
                    <tbody>
                      {expenseForm.lines.map((line, index) => {
                        const isEditing = editingLineId === `expense-${line.id}`;
                        return (
                          <tr key={line.id}>
                            <td className="modal-drag-handle">⋮⋮</td>
                            <td>{index + 1}</td>

                            {isEditing ? (
                              <>
                                <td><input type="text" value={editFormData.category} onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })} placeholder="Category" autoFocus /></td>
                                <td><input type="text" value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} placeholder="Description" /></td>
                                <td><input type="number" value={editFormData.amount} onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })} placeholder="0.00" style={{ textAlign: 'right' }} /></td>
                              </>
                            ) : (
                              <>
                                <td><input type="text" value={line.category} onChange={(e) => handleExpenseLineChange(line.id, 'category', e.target.value)} placeholder="Category" /></td>
                                <td><input type="text" value={line.description} onChange={(e) => handleExpenseLineChange(line.id, 'description', e.target.value)} placeholder="Description" /></td>
                                <td><input type="number" value={line.amount} onChange={(e) => handleExpenseLineChange(line.id, 'amount', e.target.value)} placeholder="0.00" style={{ textAlign: 'right' }} /></td>
                              </>
                            )}

                            <td>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                {isEditing ? (
                                  <>
                                    <button className="modal-delete-line-btn" style={{ color: '#10b981' }} onClick={() => handleSaveEdit(line.id, 'expense')} title="Save"><FaCheck /></button>
                                    <button className="modal-delete-line-btn" style={{ color: '#6b7280' }} onClick={handleCancelEdit} title="Cancel"><FaTimes /></button>
                                  </>
                                ) : (
                                  <>
                                    <button className="modal-delete-line-btn" style={{ color: '#004aad' }} onClick={() => handleStartEdit(line, 'expense')} title="Edit"><FaEdit /></button>
                                    <button className="modal-delete-line-btn" onClick={() => removeExpenseLine(line.id)} title="Delete"><RiDeleteBin5Fill /></button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="modal-line-items-actions">
                  <div className="modal-action-buttons"><button className="modal-add-line-btn" onClick={addExpenseLine}>Add lines</button><button className="modal-clear-lines-btn" onClick={clearAllExpenseLines}>Clear all lines</button></div>
                  <div className="modal-total-section"><span>Total</span><strong>£{calculateExpenseTotal()}</strong></div>
                </div>
              </div>

              <div className="modal-bottom-section">
                <div className="modal-form-row">
                  <div className="modal-form-group"><label>Memo</label><textarea name="memo" value={expenseForm.memo} onChange={handleExpenseInputChange} placeholder="Add a memo..." rows={4} /></div>
                  <div className="modal-form-group">
                    <label>Attachment</label>
                    {expenseForm.attachmentName ? (
                      <div className="attachment-preview-box">
                        {/* ✅ FIXED: For both local blob (new upload) and backend server (saved files) */}
                        <a
                          href={
                            expenseForm.attachmentUrl
                              ? (expenseForm.attachmentUrl.startsWith('blob:')
                                  ? expenseForm.attachmentUrl
                                  : `${BACKEND_BASE_URL}${expenseForm.attachmentUrl}`)
                              : '#'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-name"
                          style={{ color: '#004aad', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {expenseForm.attachmentName}
                        </a>
                        <div className="attachment-actions">
                          <button className="att-btn replace" onClick={() => expenseFileInputRef.current?.click()}>Change</button>
                        </div>
                        <input type="file" ref={expenseFileInputRef} onChange={handleExpenseAttachmentChange} style={{ display: 'none' }} />
                      </div>
                    ) : (
                      <label className="modal-attachment-box" htmlFor="expense-attachment">
                        <div className="modal-attachment-dropzone"><FaPaperclip className="modal-attach-icon" /><p>Add attachment</p><span>Max file size: 20 MB</span></div>
                        <input id="expense-attachment" type="file" onChange={handleExpenseAttachmentChange} style={{ display: 'none' }} />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="expense-modal-footer">
              <button className="modal-footer-btn modal-cancel-btn" onClick={closeExpenseModal}>Cancel</button>
              <div className="modal-footer-right">
                <button className="modal-footer-btn modal-save-btn" onClick={handleSaveExpense} disabled={saving}>{saving ? 'Saving...' : (editingRowId ? 'Update' : 'Save')}</button>
                {!editingRowId && (
                  <button className="modal-footer-btn modal-save-new-btn" onClick={handleSaveAndNewExpense} disabled={saving}>Save and new <span className="modal-arrow">▼</span></button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="expenses-table-container">
        {expensesLoading && (
          <div className="expenses-empty" style={{ padding: '20px', textAlign: 'center' }}>
            <p>Loading expenses…</p>
          </div>
        )}
        {!expensesLoading && renderExpensesTable()}
        {!expensesLoading && currentData.length === 0 && (
          <div className="expenses-empty">
            <FaFileInvoiceDollar className="expenses-empty-icon" />
            <p>No expenses found</p>
          </div>
        )}
      </div>

      <div className="expenses-pagination">
        <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}><FaChevronLeft /> Previous</button>
        <button className="pagination-btn" onClick={() => setCurrentPage(prev => prev + 1)}>Next <FaChevronRight /></button>
      </div>
    </div>
  );
};

export default Expenses;